import React, { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import moment from 'moment'
import { statuses } from './../TenderStatus'

import { useRouter } from '../../../../hooks'
import { useModuleActions } from '../../../../hooks/useMyPermissions'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Query, Mutation } from '../../../../api'
import md5 from 'md5'
import { useTranslation, Trans } from 'react-i18next'
import MessageReply from './components/MessageReply'

const LogItem = ({
  log,
  children,
  showEditedIndicator = false,
  onEditChild,
  editingLogId,
  onEditChildClose,
  onEditChildSubmit,
  canEdit,
}) => {
  const { t } = useTranslation()
  const sender = log.user || log.bidder || { email: '', name: 'System' }
  return (
    <div className="flex gap-3 mt-4">
      <img
        src={`https://www.gravatar.com/avatar/${md5(sender.email)}?d=mp`}
        width="36"
        height="36"
        className="rounded-full size-9 shrink-0 mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium">{sender.name}</span>
          <span className="text-xs text-muted-foreground shrink-0">
            {t('tender_timeline_date_from_now', { date: new moment(log.createdAt) })}
          </span>
        </div>
        <div className="text-sm mt-0.5">{children || log.message}</div>
        <div className="text-xs text-muted-foreground mt-1">
          {t('formatter_dateTime', { date: new moment(log.createdAt) })}
          {showEditedIndicator && log.updatedAt && (
            <span> {t('Tender_timeline_edited_label', '(edited)')}</span>
          )}
        </div>
        {log.children &&
          log.children.map((childLog, i) => {
            return (
              <ChildMessage
                key={i}
                log={childLog}
                editingLogId={editingLogId}
                onEditExpand={onEditChild}
                onEditClose={onEditChildClose}
                onEditSubmit={onEditChildSubmit}
                canEdit={canEdit}
              />
            )
          })}
      </div>
    </div>
  )
}

export default () => {
  const { t } = useTranslation()
  const [count, setCount] = useState(5)
  const [expandedReplyInputKey, setExpandedReplyInputKey] = useState(null)
  const [editingLogId, setEditingLogId] = useState(null)
  const messageInputRef = useRef()
  const {
    query: { id, projectId },
  } = useRouter()
  const { canEdit } = useModuleActions('PROCUREMENT', projectId)
  const { data, loading } = useQuery(Query.TENDER, { variables: { id } })
  const [sendMessage, sendMessageData] = useMutation(Mutation.TENDER_MESSAGE, {
    variables: { tenderId: id },
    refetchQueries: [{ query: Query.TENDER, variables: { id } }],
  })

  const handleSend = async () => {
    await sendMessage({ variables: { message: messageInputRef.current.value } })
    messageInputRef.current.value = ''
  }

  if (!data) return null
  if (!data.tender) return null

  const messages = data.tender.logs.filter(l => l.type === 'MESSAGE')

  return (
    <div>
      {canEdit && (
        <div className="flex gap-2 mb-4">
          <Input
            type="text"
            ref={messageInputRef}
            disabled={sendMessageData.loading}
            onKeyDown={({ keyCode }) => keyCode === 13 && handleSend()}
            placeholder={t('Tender_timeline_write_a_message')}
          />
          <Button
            variant="outline"
            disabled={sendMessageData.loading}
            onClick={handleSend}
            className="shrink-0"
          >
            {sendMessageData.loading && <Spinner size="sm" className="text-current" />}
            {t('Tender_timeline_send_btn')}
          </Button>
        </div>
      )}
      <div>
        {messages.slice(0, count).map((l, i) => (
          <React.Fragment key={i}>
            {
              <Message
                log={l}
                tenderId={id}
                canEdit={canEdit}
                onReplyInputExpand={() => {
                  setExpandedReplyInputKey(i)
                  setEditingLogId(null)
                }}
                onReplyInputClose={() => {
                  setExpandedReplyInputKey(null)
                }}
                onReplySubmit={() => {
                  setExpandedReplyInputKey(null)
                }}
                onEditExpand={(logId) => {
                  setEditingLogId(logId)
                  setExpandedReplyInputKey(null)
                }}
                onEditClose={() => {
                  setEditingLogId(null)
                }}
                onEditSubmit={() => {
                  setEditingLogId(null)
                }}
                expanded={
                  expandedReplyInputKey !== null && expandedReplyInputKey === i ? true : false
                }
                editingLogId={editingLogId}
              />
            }
          </React.Fragment>
        ))}
      </div>

      {messages.length > 5 && (
        <Button variant="link" className="w-full mt-2" onClick={() => setCount(count === 5 ? 1000 : 5)}>
          {`${t('Tender_timeline_show_label')} ${
            count === 5 ? t('Tender_timeline_more_label') : t('Tender_timeline_less_label')
          } ...`}
        </Button>
      )}
    </div>
  )
}

const ChildMessage = ({ log, editingLogId, onEditExpand, onEditClose, onEditSubmit, canEdit }) => {
  const { t } = useTranslation()
  const { query: { id: tenderId } } = useRouter()

  const [updateLogMessage, updateLogMessageData] = useMutation(Mutation.UPDATE_LOG_MESSAGE, {
    refetchQueries: [{ query: Query.TENDER, variables: { id: tenderId } }],
  })

  const handleEdit = async (message, isPublic, logId) => {
    await updateLogMessage({
      variables: { id: logId, message: message, isPublic: isPublic },
    })
  }

  const isEditing = editingLogId === log.id

  return (
    <LogItem log={log} showEditedIndicator={true}>
      {!isEditing && (
        <>
          <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm">{log.message}</div>
          {log.mine && canEdit && (
            <Button
              variant="link"
              size="sm"
              className="float-right"
              onClick={() => {
                onEditExpand && onEditExpand(log.id)
              }}
            >
              {t('Tender_timeline_edit_btn', 'Edit')}
            </Button>
          )}
        </>
      )}
      {isEditing && (
        <MessageReply
          editMode={true}
          initialMessage={log.message}
          initialIsPublic={log.public}
          loading={updateLogMessageData.loading}
          onExpand={() => {}}
          onClose={() => {
            onEditClose && onEditClose()
          }}
          onSubmit={(message, isPublic) => {
            handleEdit(message, isPublic, log.id)
            onEditSubmit && onEditSubmit()
          }}
          expanded={true}
        />
      )}
    </LogItem>
  )
}

const Message = ({
  log,
  tenderId,
  onReplyInputExpand,
  onReplyInputClose,
  onReplySubmit,
  onEditExpand,
  onEditClose,
  onEditSubmit,
  expanded,
  editingLogId,
  canEdit,
}) => {
  const { t } = useTranslation()

  const [sendMessageReply, sendMessageReplyData] = useMutation(Mutation.TENDER_MESSAGE, {
    variables: { tenderId: tenderId },
    refetchQueries: [{ query: Query.TENDER, variables: { id: tenderId } }],
  })

  const [updateLogMessage, updateLogMessageData] = useMutation(Mutation.UPDATE_LOG_MESSAGE, {
    refetchQueries: [{ query: Query.TENDER, variables: { id: tenderId } }],
  })

  const handleReply = async (message, isPublic, parentId) => {
    await sendMessageReply({
      variables: { message: message, parentId: parentId, isPublic: isPublic },
    })
  }

  const handleEdit = async (message, isPublic, logId) => {
    await updateLogMessage({
      variables: { id: logId, message: message, isPublic: isPublic },
    })
  }

  const isEditing = editingLogId === log.id

  switch (log.type) {
    case 'MESSAGE':
      return (
        <LogItem
          log={log}
          showEditedIndicator={true}
          editingLogId={editingLogId}
          canEdit={canEdit}
          onEditChild={(logId) => {
            onEditExpand && onEditExpand(logId)
          }}
          onEditChildClose={() => {
            onEditClose && onEditClose()
          }}
          onEditChildSubmit={() => {
            onEditSubmit && onEditSubmit()
          }}
        >
          {!isEditing && <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm">{log.message}</div>}
          {isEditing && (
            <MessageReply
              editMode={true}
              initialMessage={log.message}
              initialIsPublic={log.public}
              loading={updateLogMessageData.loading}
              onExpand={() => {}}
              onClose={() => {
                onEditClose && onEditClose()
              }}
              onSubmit={(message, isPublic) => {
                handleEdit(message, isPublic, log.id)
                onEditSubmit && onEditSubmit()
              }}
              expanded={true}
            />
          )}
          {!isEditing && log.mine && canEdit && (
            <Button
              variant="link"
              size="sm"
              className="float-right"
              onClick={() => {
                onEditExpand && onEditExpand(log.id)
              }}
            >
              {t('Tender_timeline_edit_btn', 'Edit')}
            </Button>
          )}
          {log.children.length === 0 && !isEditing && canEdit && (
            <MessageReply
              loading={sendMessageReplyData.loading}
              onExpand={() => {
                onReplyInputExpand && onReplyInputExpand()
              }}
              onClose={() => {
                onReplyInputClose && onReplyInputClose()
              }}
              onSubmit={(message, isPublic) => {
                handleReply(message, isPublic, log.id)
                onReplySubmit && onReplySubmit(message, isPublic)
              }}
              expanded={expanded}
            />
          )}
        </LogItem>
      )
    case 'EVENT':
      if (log.message && log.data.round && log.message === 'BID_SENT') {
        return (
          <LogItem log={log}>
            <strong className="truncate">
              <Trans i18nKey="Tender_timeline_message_event_BID_SENT" round={log.data.round}>
                sent bid for round {{ round: log.data.round }}
              </Trans>
            </strong>
          </LogItem>
        )
      }
      return (
        <LogItem log={log}>
          <strong className="truncate">
            {t('Tender_timeline_message_event_' + log.message)}
          </strong>
        </LogItem>
      )
    case 'STATE_CHANGE':
      return (
        <LogItem log={log}>
          <strong className="truncate">{t('Tender_timeline_state_change_by')}</strong>
          <br />
          <span className={statuses[log.data.before.status]?.textClass || ''}>
            {t(statuses[log.data.before.status]?.title || log.data.before.status)}
          </span>
          {' -> '}
          <span className={statuses[log.data.after.status]?.textClass || ''}>
            {t(statuses[log.data.after.status]?.title || log.data.after.status)}
          </span>
        </LogItem>
      )
  }
}

const SpinnerButton = () => <Spinner size="sm" className="text-current" />
